// src/CurrencyContext.js
// App-wide currency + locale for price display. Amounts in the app are stored
// in USD (matching the backend default); this context converts to the selected
// currency and formats via Intl.NumberFormat so prices are never hardcoded to
// "$". Selection persists to localStorage and syncs to the user's profile when
// signed in.
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { auth as authAPI } from './api';

// Units per 1 USD.
//
// These were hand-entered and had drifted, which is why this table was duplicated here at
// all: it claimed INR 83 while the corrected backend snapshot says 96.38, so every rupee
// price in the UI was ~14% low, and EUR/GBP/AUD/CAD/SGD were all similarly off. The values
// below now match backend/src/services/currency.js exactly, which is itself pinned to a
// dated, cross-checked snapshot (backend/src/services/rates.ingest.json) by a test.
//
// "Kept in sync by hand" is the original defect and it has now bitten twice, so the real fix
// is to serve these from the backend and delete this copy. Doing that properly means
// fetching before first paint to avoid flashing unconverted USD, which is why it is left as
// a follow-up rather than smuggled in here.
export const CURRENCIES = {
  USD: { name: 'US Dollar', symbol: '$', rate: 1 },
  EUR: { name: 'Euro', symbol: '€', rate: 0.88731 },
  GBP: { name: 'British Pound', symbol: '£', rate: 0.757129 },
  JPY: { name: 'Japanese Yen', symbol: '¥', rate: 157.923 },
  AUD: { name: 'Australian Dollar', symbol: 'A$', rate: 1.44292 },
  CAD: { name: 'Canadian Dollar', symbol: 'C$', rate: 1.42325 },
  INR: { name: 'Indian Rupee', symbol: '₹', rate: 96.3789 },
  SGD: { name: 'Singapore Dollar', symbol: 'S$', rate: 1.28049 },
  AED: { name: 'UAE Dirham', symbol: 'د.إ', rate: 3.6725 },
  CNY: { name: 'Chinese Yuan', symbol: '¥', rate: 6.71455 },
};

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCIES);

const STORAGE_KEY = 'gb_currency';
const DEFAULT_CURRENCY = 'USD';

// Map an i18n language to a sensible BCP-47 locale for number formatting.
const LOCALE_BY_LANG = { en: 'en-US', es: 'es-ES' };

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children, language = 'en', user = null }) {
  const [currency, setCurrencyState] = useState(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return stored && SUPPORTED_CURRENCIES.includes(stored) ? stored : DEFAULT_CURRENCY;
  });

  // Adopt the signed-in user's saved preference (once, on login).
  useEffect(() => {
    if (user?.preferredCurrency && SUPPORTED_CURRENCIES.includes(user.preferredCurrency)) {
      setCurrencyState(user.preferredCurrency);
    }
  }, [user?.preferredCurrency]);

  const setCurrency = useCallback((next) => {
    if (!SUPPORTED_CURRENCIES.includes(next)) return;
    setCurrencyState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    // Best-effort persistence to the profile; ignore failures (e.g. logged out).
    authAPI.updatePreferences({ preferredCurrency: next }).catch(() => {});
  }, []);

  const locale = LOCALE_BY_LANG[language] || 'en-US';

  // Convert an amount expressed in `fromCurrency` (default USD) to the active
  // currency and format it. Falls back to a symbol + number if Intl fails.
  const formatPrice = useCallback((amount, { fromCurrency = 'USD', maximumFractionDigits } = {}) => {
    if (amount == null || Number.isNaN(Number(amount))) return '';
    const fromRate = CURRENCIES[fromCurrency]?.rate ?? 1;
    const toRate = CURRENCIES[currency]?.rate ?? 1;
    const converted = (Number(amount) / fromRate) * toRate;
    const isZeroDecimal = currency === 'JPY';
    const opts = {
      style: 'currency',
      currency,
      maximumFractionDigits: maximumFractionDigits ?? (isZeroDecimal ? 0 : 2),
      minimumFractionDigits: 0,
    };
    try {
      return new Intl.NumberFormat(locale, opts).format(converted);
    } catch {
      return `${CURRENCIES[currency]?.symbol || ''}${Math.round(converted).toLocaleString()}`;
    }
  }, [currency, locale]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    locale,
    formatPrice,
    currencies: CURRENCIES,
    supportedCurrencies: SUPPORTED_CURRENCIES,
  }), [currency, setCurrency, locale, formatPrice]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return ctx;
}
